import { useCachedFetch } from '@structural-climbing/auth'
import type { Discipline } from '@structural-climbing/validation'

export interface StandingEntry {
  userId: string
  displayName: string
  avatarExt: string | null
  avatarUpdatedAt: string | null
  gender: string | null
  totalScore: number
  routesClimbed: number
  flashesCount: number
  rank: number
}

export interface StandingsData {
  standings: StandingEntry[]
  /** Your own row, ranked against everyone — null when private or not yet climbing. */
  myEntry: StandingEntry | null
  myIsPrivate: boolean
}

export type StandingsPeriod = 'week' | 'month' | 'all'
export type StandingsGender = 'all' | 'male' | 'female'

export function useStandings(
  gymId: string | null | undefined,
  apiUrl: string,
  getAccessTokenSilently: () => Promise<string>,
  period: StandingsPeriod,
  gender: StandingsGender,
  // Lead and bouldering are scored on different scales, so each has its own board.
  discipline: Discipline = 'boulder',
) {
  // Key includes every filter so each combination is cached separately and
  // switching filters doesn't serve the wrong slice from cache.
  const { data, loading, error } = useCachedFetch<StandingsData>(
    gymId ? `standings:${gymId}:${discipline}:${period}:${gender}` : null,
    async () => {
      const token = await getAccessTokenSilently()
      const params = new URLSearchParams({ gym_id: gymId!, period, gender, discipline })
      const res = await fetch(`${apiUrl}/gym-standings?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error('Failed to load standings')
      return (await res.json()) as StandingsData
    },
  )

  return { data: data ?? null, loading, error: error ? 'Failed to load standings' : null }
}
